import { formatDecimal } from "@globals/format";

type MetaLike = Pick<MQTTTopic, "valueType" | "decimals" | "unit"> | undefined;

/**
 * Formátuje hodnotu MQTT metriky podle metadata z DB (valueType / decimals / unit).
 *
 * - FLOAT  → toFixed(decimals) + " " + unit
 * - BOOLEAN → trueLabel / falseLabel (default "Zapnuto" / "Vypnuto")
 * - STRING → raw nebo přes enumMap (pro PIP mode, Tracer stage)
 *
 * Pokud metadata ještě nedorazila, vykreslí raw hodnotu (vždy něco zobrazí, žádný flash "—").
 */
export const formatMetric = (
  value: number | boolean | string | null | undefined,
  meta: MetaLike,
  options: {
    fallback?: string;
    trueLabel?: string;
    falseLabel?: string;
    enumMap?: Record<string, string>;
  } = {}
): string => {
  const { fallback = "—", trueLabel = "Zapnuto", falseLabel = "Vypnuto", enumMap } = options;

  if (value === null || value === undefined) return fallback;

  if (!meta) {
    return String(value);
  }

  if (meta.valueType === "BOOLEAN") {
    const b = typeof value === "boolean" ? value : value === "true" || value === 1 || value === "1";
    return b ? trueLabel : falseLabel;
  }

  if (meta.valueType === "STRING") {
    const s = String(value);
    return enumMap?.[s] ?? s;
  }

  // FLOAT
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return fallback;
  const fixed = meta.decimals > 0 ? formatDecimal(n, meta.decimals) : String(Math.round(n));
  return meta.unit ? `${fixed} ${meta.unit}` : fixed;
};

/**
 * Vrátí label pro metriku — nickname z DB, fallback na poskytnutý string (nebo prázdný).
 */
export const metricLabel = (meta: Pick<MQTTTopic, "nickname" | "topic"> | undefined, fallback = ""): string => {
  if (!meta) return fallback;
  return meta.nickname || meta.topic || fallback;
};
